import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, Share2, GripVertical } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const socialPlatforms = [
  { value: "linkedin", label: "LinkedIn", icon: "linkedin" },
  { value: "twitter", label: "X (Twitter)", icon: "twitter" },
  { value: "facebook", label: "Facebook", icon: "facebook" },
  { value: "instagram", label: "Instagram", icon: "instagram" },
  { value: "youtube", label: "YouTube", icon: "youtube" },
  { value: "tiktok", label: "TikTok", icon: "tiktok" },
  { value: "spotify", label: "Spotify", icon: "spotify" },
  { value: "apple", label: "Apple Podcasts", icon: "apple" },
  { value: "website", label: "Website", icon: "globe" },
];

const SocialLinksManager = () => {
  const [links, setLinks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [formData, setFormData] = useState({
    platform: "",
    url: "",
    icon: "",
    is_active: true,
    order: 0
  });

  useEffect(() => {
    fetchLinks();
  }, []);

  const getAuthHeader = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
  });

  const fetchLinks = async () => {
    try {
      const response = await axios.get(`${API}/social-links`, getAuthHeader());
      setLinks(response.data);
    } catch (error) {
      toast.error("Failed to fetch social links");
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      platform: "",
      url: "",
      icon: "",
      is_active: true,
      order: links.length
    });
    setEditingLink(null);
  };

  const handleOpenDialog = (link = null) => {
    if (link) {
      setEditingLink(link);
      setFormData({
        platform: link.platform || "",
        url: link.url || "",
        icon: link.icon || "",
        is_active: link.is_active ?? true,
        order: link.order || 0
      });
    } else {
      resetForm();
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    resetForm();
  };

  const handlePlatformChange = (value) => {
    const platform = socialPlatforms.find(p => p.value === value);
    setFormData({
      ...formData,
      platform: value,
      icon: platform?.icon || value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      if (editingLink) {
        await axios.put(`${API}/social-links/${editingLink.id}`, formData, getAuthHeader());
        toast.success("Social link updated");
      } else {
        await axios.post(`${API}/social-links`, formData, getAuthHeader());
        toast.success("Social link added");
      }
      handleCloseDialog();
      fetchLinks();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save social link");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this social link?")) return;

    try {
      await axios.delete(`${API}/social-links/${id}`, getAuthHeader());
      toast.success("Social link deleted");
      fetchLinks();
    } catch (error) {
      toast.error("Failed to delete social link");
    }
  };

  const toggleActive = async (link) => {
    try {
      await axios.put(`${API}/social-links/${link.id}`, {
        ...link,
        is_active: !link.is_active
      }, getAuthHeader());
      fetchLinks();
    } catch (error) {
      toast.error("Failed to update link");
    }
  };

  return (
    <div data-testid="social-links-manager">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-3xl font-semibold text-[#1A2323] mb-2">Social Links</h1>
          <p className="text-[#4A5555]">Manage your social media links</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              className="bg-[#2E8B8B] hover:bg-[#1F6666] text-white"
              onClick={() => handleOpenDialog()}
              data-testid="add-social-link-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Link
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-serif text-xl">
                {editingLink ? "Edit Social Link" : "Add Social Link"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="platform">Platform</Label>
                <Select 
                  value={formData.platform} 
                  onValueChange={handlePlatformChange}
                >
                  <SelectTrigger data-testid="platform-select">
                    <SelectValue placeholder="Select platform" />
                  </SelectTrigger>
                  <SelectContent>
                    {socialPlatforms.map((platform) => (
                      <SelectItem key={platform.value} value={platform.value}>
                        {platform.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="url">URL</Label>
                <Input
                  id="url"
                  type="url"
                  placeholder="https://..."
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  required
                  data-testid="social-url-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="order">Display Order</Label>
                <Input
                  id="order"
                  type="number"
                  placeholder="0"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                  data-testid="social-order-input"
                />
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  data-testid="social-active-switch"
                />
                <Label htmlFor="is_active">Active</Label>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={handleCloseDialog}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="bg-[#2E8B8B] hover:bg-[#1F6666] text-white"
                  disabled={isSaving}
                  data-testid="save-social-link-btn"
                >
                  {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {editingLink ? "Update" : "Add"} Link
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-[#E5E0D6]">
        <CardHeader>
          <CardTitle className="font-serif text-lg text-[#1A2323]">Your Social Links</CardTitle>
          <CardDescription>These links will appear on your landing page</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-[#2E8B8B]" />
            </div>
          ) : links.length === 0 ? (
            <div className="text-center py-8">
              <Share2 className="w-12 h-12 text-[#E67E22] mx-auto mb-4 opacity-50" />
              <p className="text-[#4A5555]">No social links yet. Add your first link!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {links.map((link) => (
                <div 
                  key={link.id}
                  className="flex items-center gap-4 p-4 rounded-lg bg-[#F5F2EB] hover:bg-[#E5E0D6]/50 transition-colors"
                  data-testid={`social-link-${link.id}`}
                >
                  <GripVertical className="w-5 h-5 text-[#4A5555] cursor-grab" />
                  <div className="w-10 h-10 rounded-full bg-[#2E8B8B] text-white flex items-center justify-center">
                    <span className="text-xs font-bold uppercase">{link.platform?.charAt(0)}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-[#1A2323] capitalize">{link.platform}</p>
                    <p className="text-sm text-[#4A5555] truncate">{link.url}</p>
                  </div>
                  <Switch
                    checked={link.is_active}
                    onCheckedChange={() => toggleActive(link)}
                    data-testid={`toggle-social-${link.id}`}
                  />
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => handleOpenDialog(link)}
                    data-testid={`edit-social-${link.id}`}
                  >
                    <Pencil className="w-4 h-4 text-[#4A5555]" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => handleDelete(link.id)}
                    data-testid={`delete-social-${link.id}`}
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SocialLinksManager;
